import {
  MeetingStepFragment,
  Meeting_Step_Type_Enum,
  Task_Status_Enum,
} from '../gql'
import { MeetingStepConfig } from './meeting'
import { MeetingThreadsSortMode, ThreadsWeather } from './threadWeather'
import {
  RoleReviewBy,
  RoleReviewLabelKey,
  RoleReviewMode,
  RoleReviewTextKey,
} from './roleReview'
import { TasksViewTypes } from './task'

/*** Meeting Step
 *
 * Codegen uses schema overrides and types from this file to generate types.
 * Please check those files if you want to change the schema.
 * - schema-overrides.gql
 * - codegen.yml
 */

// Tour
export interface MeetingStepDataTour {}

export interface MeetingStepTourFragment extends MeetingStepFragment {
  type: Meeting_Step_Type_Enum.Tour
  data: MeetingStepDataTour
}

// Inclusion and déclusion: the rounds that open and close a meeting. They hold
// a shared note, exactly like Tour above, and exist as their own types so the
// ritual has a name in the "add a step" menu — and so an outfitted inclusion
// (the weather round, where each person posts how they are) can be built later
// without having to guess which free notes were inclusions (D5, 05/08/2026).
export interface MeetingStepInclusionFragment extends MeetingStepFragment {
  type: Meeting_Step_Type_Enum.Inclusion
  data: MeetingStepDataTour
}

export interface MeetingStepDeclusionFragment extends MeetingStepFragment {
  type: Meeting_Step_Type_Enum.Declusion
  data: MeetingStepDataTour
}

// Threads
//
// `threadsIds` is the agenda: the ordered list, written by drag & drop alone.
// The three keys below belong to #89 and are all optional — their absence is
// the normal state of every step created before it (REU89-D5).
//
// 🔑 They die with the meeting (REU89-D2): the next one starts unclassified.
// 🔑 They are written through `_append`, which merges at the FIRST level of a
// jsonb column (REU89-D3). So each key must stay a first-level one: writing a
// weather rewrites the whole `threadsWeather` map but can no longer destroy
// `threadsIds`, which was the silent data loss of #46.
export interface MeetingStepDataThreads {
  threadsIds: string[]
  // What each participant classified today. A missing entry displays as the
  // default weather, without any write.
  threadsWeather?: ThreadsWeather
  // Which of the four reading orders everyone sees — the meeting is
  // synchronised, so the order is shared, not personal (REU89-D7).
  sortMode?: MeetingThreadsSortMode
  // The order of the round in "by member", set by hand by any participant.
  // Participants missing from it go last (REU89-D11).
  membersOrder?: string[]
}

export interface MeetingStepThreadsFragment extends MeetingStepFragment {
  type: Meeting_Step_Type_Enum.Threads
  data: MeetingStepDataThreads
}

// Governance threads. Same shape as the step above: the two differ by which
// threads they accept, never by what they hold. `Threads` was kept as-is rather
// than renamed to `ThreadsTactical`, because a step's type lives as a string
// inside JSON in two tables (meeting.stepsConfig, meeting_template.stepsConfig)
// and renaming it would mean rewriting that JSON everywhere (D4, 05/08/2026).
export interface MeetingStepGovernanceThreadsFragment extends MeetingStepFragment {
  type: Meeting_Step_Type_Enum.ThreadsGovernance
  data: MeetingStepDataThreads
}

// Checklist: the checklists of the circle's roles and of its direct children,
// each shown as its own block (REU-04).
//
// 🔑 `checked` lives here and never in the role. A checklist is a reminder of
// recurring actions "pas encore acquises par le rôle" (REU04-D4): ticking a box
// in a meeting says what was done that day, so the role stays the clean
// template and every meeting starts blank. Items are keyed by position, the
// only handle a text column offers — see checklistItems.ts.
//
// Both fields are optional: steps created before REU-04 carry an empty data and
// must keep opening.
//
// 🔑 A tick is written as its own key at the root, `checked:<roleId>:<index>`
// = true or false, merged by `_append` (#46, 25/09/2026). Rewriting the whole
// `checked` object, as before, let two people ticking at the same instant
// erase each other's box; one key per box makes that impossible, even on the
// same role. `checked` is the former format, still read for past meetings.
export type ChecklistTickKey = `checked:${string}:${number}`

export interface MeetingStepDataChecklist {
  checked?: Record<string, number[]>
  filterRolesIds?: string[]
  [key: ChecklistTickKey]: boolean | undefined
}

export function checklistTickKey(
  roleId: string,
  index: number
): ChecklistTickKey {
  return `checked:${roleId}:${index}`
}

// The ticked boxes, by role: the former `checked` object first, then every
// root key on top of it (a later untick wins over an old tick).
export function checklistCheckedByRole(
  data: MeetingStepDataChecklist | null | undefined
): Record<string, number[]> {
  const byRole = new Map<string, Set<number>>()
  const boxes = (roleId: string) => {
    let set = byRole.get(roleId)
    if (!set) byRole.set(roleId, (set = new Set()))
    return set
  }
  for (const [roleId, indices] of Object.entries(data?.checked ?? {})) {
    for (const index of indices) boxes(roleId).add(index)
  }
  for (const [key, value] of Object.entries(data ?? {})) {
    const match = /^checked:(.+):(\d+)$/.exec(key)
    if (!match) continue
    const [, roleId, index] = match
    if (value === true) boxes(roleId).add(Number(index))
    else if (value === false) boxes(roleId).delete(Number(index))
  }
  const result: Record<string, number[]> = {}
  for (const [roleId, set] of byRole) {
    if (set.size) result[roleId] = [...set].sort((a, b) => a - b)
  }
  return result
}

export interface MeetingStepChecklistFragment extends MeetingStepFragment {
  type: Meeting_Step_Type_Enum.Checklist
  data: MeetingStepDataChecklist
}

// Indicators: same blocks, but editing one writes straight into the role — an
// indicator is a current state, not a per-meeting tally (REU04-D2, D16). Hence
// a filter here, and nothing else to remember.
export interface MeetingStepDataIndicators {
  filterRolesIds?: string[]
}

export interface MeetingStepIndicatorsFragment extends MeetingStepFragment {
  type: Meeting_Step_Type_Enum.Indicators
  data: MeetingStepDataIndicators
}

// Tasks
export interface MeetingStepDataTasks {
  viewType: TasksViewTypes
  filterStatus: Task_Status_Enum | null
  filterMemberId: string | null
  // Role review (#80): the step reviews projects (default) or takes one text
  // per person or per role. See roleReview.ts.
  reviewMode?: RoleReviewMode
  reviewBy?: RoleReviewBy
  // Speaking order of the "by person" view, as the tensions' round (REU89-D11)
  reviewMembersOrder?: string[]
  // Order of the "by role" view when the texts were written, for the summary
  reviewRolesOrder?: string[]
  // One text per person or role, each at the ROOT of `data`: `_append` only
  // merges the first level, so a nested map would be rewritten whole by every
  // save and two writers would erase each other (#46).
  [key: RoleReviewTextKey]: string
  [key: RoleReviewLabelKey]: string
}

export interface MeetingStepTasksFragment extends MeetingStepFragment {
  type: Meeting_Step_Type_Enum.Tasks
  data: MeetingStepDataTasks
}

// Next steps: the circle's next steps, shown as a two-column board. Nothing is
// stored on the step itself — unlike Tasks, it has no view type and no filter:
// the board always shows every unarchived next step of the circle, across all
// meetings (D24), and the "show more" of a column is transient screen state,
// not a decision worth persisting (D23).
export interface MeetingStepDataNextSteps {}

export interface MeetingStepNextStepsFragment extends MeetingStepFragment {
  type: Meeting_Step_Type_Enum.NextSteps
  data: MeetingStepDataNextSteps
}

export type MeetingStepData =
  | MeetingStepDataTour
  | MeetingStepDataThreads
  | MeetingStepDataChecklist
  | MeetingStepDataIndicators
  | MeetingStepDataTasks
  | MeetingStepDataNextSteps

// No role parameter since REU-04: the two steps that read one did so only to
// copy its text into their notes, and that copy is gone. Callers no longer need
// to fetch the circle's role before creating steps.
export function getDefaultMeetingStep(
  meetingId: string,
  stepConfig: MeetingStepConfig
): Omit<MeetingStepFragment, 'id'> {
  const type = stepConfig.type
  const step = {
    meetingId,
    stepConfigId: stepConfig.id,
    notes: '',
    data: {},
  }

  switch (type) {
    case Meeting_Step_Type_Enum.Tour:
    case Meeting_Step_Type_Enum.Inclusion:
    case Meeting_Step_Type_Enum.Declusion:
      return { ...step, type }

    case Meeting_Step_Type_Enum.Threads:
    case Meeting_Step_Type_Enum.ThreadsGovernance: {
      return {
        ...step,
        type,
        data: {
          threadsIds: [],
        },
      }
    }

    case Meeting_Step_Type_Enum.Tasks:
      return {
        ...step,
        type,
        data: {
          viewType: TasksViewTypes.Kanban,
          filterMemberId: null,
          filterStatus: null,
        },
      }

    // Both used to be born with a copy of the role's text in their notes, made
    // once and frozen. REU-04 replaced that copy with a live view of every role
    // of the circle and of its direct children, so copying now would show the
    // same thing twice, one version current and one stale (REU04-D19).
    //
    // ⚠️ What the removal takes with it: that copy was the only memory of the
    // value on the day of the meeting. Assumed (REU04-D16) — an indicator is a
    // current state, and freezing it is the job of the end-of-meeting email.
    case Meeting_Step_Type_Enum.Checklist:
    case Meeting_Step_Type_Enum.Indicators:
      return { ...step, type }

    // Deliberately its own case rather than grouped with Tour, Inclusion and
    // Declusion: those three are note-taking rounds, this one is a board. They
    // happen to build the same object today; saying so out loud would be a
    // coincidence dressed up as a rule.
    case Meeting_Step_Type_Enum.NextSteps:
      return { ...step, type }
  }
}
